import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { showToast } from '../../utils/toast';
import { postApi } from '../../utils/api/api';
import DeleteImages from '../Common/DeleteImages';
import { FileSpreadsheet } from 'lucide-react';
import { FaDollarSign, FaBox } from "react-icons/fa";

const EditInventoryForm = () => {
  const baseUrl = "https://backend.tokoyarn.com/public/";
  const location = useLocation();
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const inventoryData = location.state?.inventory || null;
  const [isLocal, setIsLocal] = useState(false);
  const [selectedOption, setSelectedOption] = useState("$ / kg");

  const handleChange = (e) => {
    setSelectedOption(e.target.value);
  };

  // Common state for both forms
  const [formData, setFormData] = useState({
    type: '',
    images: [],
    certificate: null,
    ipi: '',
    quality_grade: '',
    category: '',
    sub_category: '',
    end_use: '',
    technology: '',
    ply: '',
    application: '',
    yarn_speciality: '',
    // Spun specific fields
    material: '',
    qty: '',
    qty_type: '',
    price: '',
    dying: '',
    count_from: '',
    count_to: '',
    avg_csp_from: '',
    avg_csp_to: '',
    spinning_process: '',
    spinning_type: '',
    blend1: '',
    blend1_percentage: '',
    blend2: '',
    blend2_percentage: '',
    // Filament specific fields
    filament_categories: '',
    filament_priceperkg_from: '',
    filament_priceperkg_to: '',
    filament_quantity_from: '',
    filament_quantity_to: '',
    semi_full_dull: '',
    texturized: '',
    filament_dennier_from: '',
    filament_dennier_to: '',
    filament_no_from: '',
    filament_no_to: '',
    filament_twist: '',
    roto_twist_intermingle: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showBlendFields, setShowBlendFields] = useState(false);

  // Options for dropdowns
  const materialsOptions = ["Cotton 100%","PES 100%",'TC PES 60% Cotton 40%','TC PES 52% Cotton 48%','CVC Cotton 60% PES 40%',
                          'CVC Cotton 52% PES 48%','TR PES 60% Rayon 40%','TR PES 65% Rayon 35%','Rayon 100% Raw','Rayon 100% White',
                          'LWA Eco','Modal','Bamboo','Blends Specify','Filament DTY','Filament FDY','Filament SDY','Nylon','Misty/Melange M61','Misty/Melange M71','Misty/Melange M81',
                            'Misty/Melange 100% Black','Sewing Threads','Specify'];
  const spinningTypeOptions = ['Carded','Combed','Compact','Non compact','Semi-dull','Bright','Super Bright','High Tenacity'];
  const spinningProcessOptions = ["Open end","Ring","MVS","Texturizing"];
  const qualityGradeOptions = ["A","B"];
  const dyingOptions = ["Print quality","Dying quality",'Both'];
  const quantityTypeOptions = ['Kg', 'Bundle'];
  const categoryOptions = ['Cotton','Polyester','Viscose','Linen'];
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const endUseOptions = ['Weaving','Knitting/Hosiery','Sewing Thread','Embroidery','Filtration'];
  const technologyOptions = ['Ring','Open End','Vortex','Airjet','Wet','Friction'];
  const plyOptions = ['Single', 'Double', 'Multi-Fold'];
  const applicationOptions = ['Warp','Weft','Warp&Weft','Circular','Filter'];
  const yarnSpecialityOptions = ['Slub','High-twist','Organic','BCI','Neps','Siro/ Elli Twist','Injected Slub','Super High Twist','TFO'];
  const blendOptions = ["Cotton Carded", "Cotton Combed", "Giza", "Supima", "Pima", "BCI Cotton", "Polyester", "Micro Polyester", 
    "Recycled Polyester", "Viscose", "Micro Viscose", "Liva Eco", "Ecovera", "Modal", "Micro Modal", "Excel", "Micro Excel", 
    "Tencel", "Micro Tencel", "Acrylic", "Linen", "Remie", "Hemp", "Bamboo", "Banana", "Soya", "Nylon", "Cupro", "Silk"];
  const filamentCategoriesOptions = ['POY/FOY', 'Viscose', 'Nylon'];
  const semiFullDullOptions = [
    { value: 'ROTO', label: 'ROTO' },
    { value: 'Draw Twist', label: 'Draw Twist' },
    { value: 'Airtex', label: 'Airtex' },
    { value: 'Mono', label: 'Mono' },
    { value: 'Kota', label: 'Kota' },
    { value: 'Super Glue', label: 'Super Glue' },
    { value: 'Glue', label: 'Glue' }
  ];
  const texturizedOptions = [
    { value: 'Bright', label: 'Bright' },
    { value: 'Catonic', label: 'Catonic' },
    { value: 'Easy Dyable', label: 'Easy Dyable' }
  ];

  // Update sub-category options based on selected category
  useEffect(() => {
    if (formData.category === 'Cotton') {
      setSubCategoryOptions(['Carded', 'Combed', 'Combed Compact', 'BCI', 'Organic', 'Giza', 'Giza Blend', 'Supima', 'Pima']);
    } else if (formData.category === 'Polyester') {
      setSubCategoryOptions(['Micro', 'Recycled', 'Lycra','Semi Dull','Bright','High Tenacity']);
    } else if (formData.category === 'Viscose') {
      setSubCategoryOptions(['Micro', 'Liva Eco', 'Ecovera', 'Modal', 'Micro Modal', 'Excel', 'Micro Excel', 'Tencel', 'Micro Tencel', 'Bamboo', 'Lycra','Semi Dull','Bright','High Tenacity']);
    } else if (formData.category === 'Linen') {
      setSubCategoryOptions(['Long Fiber', 'Short Fiber']);
    } else {
      setSubCategoryOptions([]);
    }
  }, [formData.category]);

  useEffect(() => {
    if (inventoryData) {
      try {
        const parsedData = JSON.parse(JSON.stringify(inventoryData));
        const initialData = {
          type: parsedData.type,
          images: parsedData.images || [],
          certificate: baseUrl + (parsedData.certificate || ''),
          ipi: parsedData.ipi || '',
          quality_grade: parsedData.quality_grade || '',
          ...(parsedData.type === 'Spun' && parsedData.spun ? {
            category: parsedData.spun.category || '',
            sub_category: parsedData.spun.sub_category || '',
            end_use: parsedData.spun.end_use || '',
            technology: parsedData.spun.technology || '',
            ply: parsedData.spun.ply || '',
            application: parsedData.spun.application || '',
            yarn_speciality: parsedData.spun.yarn_speciality || '',
            price: parsedData.spun.price || '',
            count_from: parsedData.spun.count_from || '',
            count_to: parsedData.spun.count_to || '',
            avg_csp_from: parsedData.spun.avg_csp_from || '',
            avg_csp_to: parsedData.spun.avg_csp_to || '',
            blend1: parsedData.spun.blend1 || '',
            blend1_percentage: parsedData.spun.blend1_percentage || '',
            blend2: parsedData.spun.blend2 || '',
            blend2_percentage: parsedData.spun.blend2_percentage || ''
          } : {}),
          ...(parsedData.type === 'Filament' && parsedData.filament ? {
            filament_categories: parsedData.filament.category || '',
            filament_priceperkg_from: parsedData.filament.price_from || '',
            filament_priceperkg_to: parsedData.filament.price_to || '',
            filament_quantity_from: parsedData.filament.quantity_from || '',
            filament_quantity_to: parsedData.filament.quantity_to || '',
            semi_full_dull: parsedData.filament.dull || '',
            texturized: parsedData.filament.texturized || '',
            filament_dennier_from: parsedData.filament.dennier_from || '',
            filament_dennier_to: parsedData.filament.dennier_to || '',
            filament_no_from: parsedData.filament.filament_no_from || '',
            filament_no_to: parsedData.filament.filament_no_to || '',
            filament_twist: parsedData.filament.filament_twist || '',
            roto_twist_intermingle: parsedData.filament.roto_twist_intermingle || ''
          } : {})
        };
        setFormData(initialData);
        setShowBlendFields(!!(parsedData.spun?.blend1 && parsedData.spun?.blend2));
      } catch (error) {
        console.error('Invalid inventoryData format:', error);
        navigate('/seller-dashboard');
      }
    } else {
      navigate('/seller-dashboard');
    }
  }, [inventoryData, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (field === 'certificate' && file && !file.type.includes('pdf')) {
      setErrors((prev) => ({ ...prev, [field]: 'Please upload a PDF file only.' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: file }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleImageChange = (e) => {
    setIsLocal(true);
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(file => !['image/jpeg', 'image/png'].includes(file.type));
    
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({ ...prev, images: 'Please upload only JPG or PNG files.' }));
      return;
    }
    
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
    if (files.length > 0) {
      setErrors((prev) => ({ ...prev, images: '' }));
    }
  };

  const handleImageDelete = (index) => {
    setFormData((prev) => {
      const updatedImages = prev.images.filter((_, i) => i !== index);
      return { ...prev, images: updatedImages };
    });
  };

  const handleCertificateDelete = () => {
    setFormData((prev) => ({ ...prev, certificate: null }));
  };

  const toggleBlendFields = () => {
    setShowBlendFields(!showBlendFields);
    if (!showBlendFields) {
      setFormData(prev => ({
        ...prev,
        blend1: '',
        blend1_percentage: '',
        blend2: '',
        blend2_percentage: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Common validations
    if (!formData.quality_grade?.trim()) newErrors.quality_grade = 'Quality grade is required';
    if (!formData.ipi?.trim()) newErrors.ipi = 'IPI is required';
    /*if ((!formData.images || formData.images.length === 0) && (!inventoryData?.prevImages || inventoryData.prevImages.length === 0)) {
      newErrors.images = 'Please upload at least one image';
    }*/
    // if (!formData.certificate) newErrors.certificate = 'Please upload a certificate';

    if (formData.type === 'Spun') {
      // Spun specific validations
      // if (!formData.material?.trim()) newErrors.material = 'Material is required';
      if (!formData.category?.trim()) newErrors.category = 'Category is required';
      if (!formData.sub_category?.trim()) newErrors.sub_category = 'Sub Category is required';
      if (!formData.price?.trim()) newErrors.price =  'Price is required.';  
      // if (!formData.qty || isNaN(formData.qty)) newErrors.qty = 'Enter a valid quantity';
      // if (!formData.qty_type?.trim()) newErrors.qty_type = 'Quantity type is required';
      // if (!formData.price || isNaN(formData.price)) newErrors.price = 'Enter a valid price';
      // if (!formData.count_from || isNaN(formData.count_from)) newErrors.count_from = 'Count from is required';
      // if (!formData.count_to || isNaN(formData.count_to)) newErrors.count_to = 'Count to is required';
      // if (parseFloat(formData.count_from) > parseFloat(formData.count_to)) newErrors.count_to = 'Count To must be greater than or equal to Count From';
      
      if (showBlendFields) {
        if (!formData.blend1?.trim()) newErrors.blend1 = 'Blend 1 is required';
        if (!formData.blend1_percentage?.trim() || isNaN(formData.blend1_percentage)) newErrors.blend1_percentage = 'Enter a valid percentage';
        if (!formData.blend2?.trim()) newErrors.blend2 = 'Blend 2 is required';
        if (!formData.blend2_percentage?.trim() || isNaN(formData.blend2_percentage)) newErrors.blend2_percentage = 'Enter a valid percentage';
      }
    } else if (formData.type === 'Filament') {
      // Filament specific validations
      if (!formData.filament_categories?.trim()) newErrors.filament_categories = 'Category is required';
      if (!formData.filament_priceperkg_from || isNaN(formData.filament_priceperkg_from)) newErrors.filament_priceperkg_from = 'Enter a valid price';
      if (!formData.filament_priceperkg_to || isNaN(formData.filament_priceperkg_to)) newErrors.filament_priceperkg_to = 'Enter a valid price';
      // if (parseFloat(formData.filament_priceperkg_from) > parseFloat(formData.filament_priceperkg_to)) newErrors.filament_priceperkg_to = 'Price To must be greater than or equal to Price From';
      // if (!formData.filament_quantity_from || isNaN(formData.filament_quantity_from)) newErrors.filament_quantity_from = 'Enter a valid quantity';
      // if (!formData.filament_quantity_to || isNaN(formData.filament_quantity_to)) newErrors.filament_quantity_to = 'Enter a valid quantity';
      // if (parseFloat(formData.filament_quantity_from) > parseFloat(formData.filament_quantity_to)) newErrors.filament_quantity_to = 'Quantity To must be greater than or equal to Quantity From';
      // if (!formData.semi_full_dull?.trim()) newErrors.semi_full_dull = 'Semi/Full Dull is required';
      // if (!formData.texturized?.trim()) newErrors.texturized = 'Texturized is required';
      // if (!formData.filament_dennier_from || isNaN(formData.filament_dennier_from)) newErrors.filament_dennier_from = 'Enter a valid dennier';
      // if (!formData.filament_dennier_to || isNaN(formData.filament_dennier_to)) newErrors.filament_dennier_to = 'Enter a valid dennier';
      // if (parseFloat(formData.filament_dennier_from) > parseFloat(formData.filament_dennier_to)) newErrors.filament_dennier_to = 'Dennier To must be greater than or equal to Dennier From';
      // if (!formData.filament_no_from || isNaN(formData.filament_no_from)) newErrors.filament_no_from = 'Enter a valid number';
      // if (!formData.filament_no_to || isNaN(formData.filament_no_to)) newErrors.filament_no_to = 'Enter a valid number';
      // if (parseFloat(formData.filament_no_from) > parseFloat(formData.filament_no_to)) newErrors.filament_no_to = 'Number To must be greater than or equal to Number From';
    }

    setErrors(newErrors);
    console.log(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = { 
        ...formData,
        price_type: selectedOption
      };

      const formDataPayload = new FormData();
      Object.keys(payload).forEach((key) => {
        if (key === 'images') {
          let imagesArray = payload.images;

          if (typeof imagesArray === 'string') {
            imagesArray = JSON.parse(imagesArray);
          }

          imagesArray.forEach((image) => {
            if (image instanceof File) {
              formDataPayload.append('images[]', image);
            } else {
              console.error('Expected file, but got a string (image path):', image);
            }
          });
        } else if (key === 'certificate') {
          if (typeof payload.certificate != 'string' || payload.certificate?.indexOf('http') == -1) {
            formDataPayload.append('certificate', payload.certificate);
          }
        } else {
          formDataPayload.append(key, payload[key] || '');
        }
      });
      
      if(!formDataPayload.get('certificate')) {
        formDataPayload.append('certificate', '');
      }
      if(!formDataPayload.get('images[]')) {
        formDataPayload.append('images[]', '');
      }
      
      await postApi(`seller/edit-product/${id}`, formDataPayload);
      showToast('success', t('success.inventoryAdded', 'Inventory updated successfully!'));
      navigate('/seller-dashboard');
    } catch (err) {
      showToast('error', err.message || t('errors.submitFailed', 'Failed to submit the form.'));
    } finally {
      setLoading(false);
    }
  };

  const renderSpunForm = () => (
    <div className='row'>
      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Category<sup>*</sup></label>
          <select
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Category...</option>
            {categoryOptions.map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
          {errors.category && <small className="text-danger">{errors.category}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Sub-Category</label>
          <select
            name="sub_category"
            value={formData.sub_category}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
            disabled={!formData.category}
          >
            <option value="">Choose Sub-Category...</option>
            {subCategoryOptions.map((subCat, index) => (
              <option key={index} value={subCat}>{subCat}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">End Use</label>
          <select
            name="end_use"
            value={formData.end_use}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose End Use...</option>
            {endUseOptions.map((use, index) => (
              <option key={index} value={use}>{use}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Technology</label>
          <select
            name="technology"
            value={formData.technology}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Technology...</option>
            {technologyOptions.map((tech, index) => (
              <option key={index} value={tech}>{tech}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Count From</label>
          <input
            type="number"
            name="count_from"
            value={formData.count_from}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.count_from && <small className="text-danger">{errors.count_from}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Count To</label>
          <input
            type="number"
            name="count_to"
            value={formData.count_to}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.count_to && <small className="text-danger">{errors.count_to}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Avg CSP From</label>
          <div className="input-with-icon">
            <input
              type="number"
              name="avg_csp_from"
              value={formData.avg_csp_from}
              onChange={handleInputChange}
              className="form-control border-0 bg-light px-4"
            />
            {/*{selectedOption === "$ / kg" ? (
              <FaDollarSign size={20} color="green" />
            ) : (
              <FaBox size={20} color="blue" />
            )}*/}
          </div>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Avg CSP To</label>
          <div className="input-with-icon">
            <input
              type="number"
              name="avg_csp_to"
              value={formData.avg_csp_to}
              onChange={handleInputChange}
              className="form-control border-0 bg-light px-4"
            />
           {/* {selectedOption === "$ / kg" ? (
              <FaDollarSign size={20} color="green" />
            ) : (
              <FaBox size={20} color="blue" />
            )}*/}
          </div>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Ply</label>
          <select
            name="ply"
            value={formData.ply}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Ply...</option>
            {plyOptions.map((ply, index) => (
              <option key={index} value={ply}>{ply}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Application</label>
          <select
            name="application"
            value={formData.application}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Application...</option>
            {applicationOptions.map((app, index) => (
              <option key={index} value={app}>{app}</option>
            ))}
          </select>
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Yarn Speciality</label>
          <select
            name="yarn_speciality"
            value={formData.yarn_speciality}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Yarn Speciality...</option>
            {yarnSpecialityOptions.map((speciality, index) => (
              <option key={index} value={speciality}>{speciality}</option>
            ))}
          </select>
        </div>
      </div>
      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Price</label>
          <div className="input-with-icon">
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="form-control border-0 bg-light px-4"
            />
           
          </div>
        </div>
      </div>

      <div className='col-lg-12'>
        <div className='d-flex justify-content-between form-head-wrap'>
          <div className='d-flex form-check-wrap'>
            <label className="form-check-label" htmlFor="flexSwitchCheckDefault" style={{ marginRight: '27px' }}> Single</label>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="flexSwitchCheckDefault"
                checked={showBlendFields}
                onChange={toggleBlendFields}
              />
              <label className="form-check-label" htmlFor="flexSwitchCheckDefault">
                Blend
              </label>
            </div>
          </div>
        </div>
      </div>

      {showBlendFields && (
        <>
          <div className='col-lg-6'>
            <div className="form-group">
              <label className="form-label text-dark">Blend 1<sup>*</sup></label>
              <select
                name="blend1"
                value={formData.blend1}
                onChange={handleInputChange}
                className="form-control border-0 bg-light px-4"
              >
                <option value="">Choose Blend 1...</option>
                {blendOptions.map((blend, index) => (
                  <option key={index} value={blend}>{blend}</option>
                ))}
              </select>
              {errors.blend1 && <small className="text-danger">{errors.blend1}</small>}
            </div>
          </div>
          <div className='col-lg-6'>
            <div className="form-group">
              <label className="form-label text-dark">Blend 1 %<sup>*</sup></label>
              <input
                type="number"
                name="blend1_percentage"
                value={formData.blend1_percentage}
                onChange={handleInputChange}
                className="form-control border-0 bg-light px-4"
              />
              {errors.blend1_percentage && <small className="text-danger">{errors.blend1_percentage}</small>}
            </div>
          </div>
          <div className='col-lg-6'>
            <div className="form-group">
              <label className="form-label text-dark">Blend 2<sup>*</sup></label>
              <select
                name="blend2"
                value={formData.blend2}
                onChange={handleInputChange}
                className="form-control border-0 bg-light px-4"
              >
                <option value="">Choose Blend 2...</option>
                {blendOptions.map((blend, index) => (
                  <option key={index} value={blend}>{blend}</option>
                ))}
              </select>
              {errors.blend2 && <small className="text-danger">{errors.blend2}</small>}
            </div>
          </div>
          <div className='col-lg-6'>
            <div className="form-group">
              <label className="form-label text-dark">Blend 2 %<sup>*</sup></label>
              <input
                type="number"
                name="blend2_percentage"
                value={formData.blend2_percentage}
                onChange={handleInputChange}
                className="form-control border-0 bg-light px-4"
              />
              {errors.blend2_percentage && <small className="text-danger">{errors.blend2_percentage}</small>}
            </div>
          </div>
        </>
      )}

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">IPI</label>
          <input
            type="text"
            name="ipi"
            value={formData.ipi}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.ipi && <small className="text-danger">{errors.ipi}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Quality Grade<sup>*</sup></label>
          <select
            name="quality_grade"
            value={formData.quality_grade}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Quality Grade...</option>
            {qualityGradeOptions.map((grade, index) => (
              <option key={index} value={grade}>{grade}</option>
            ))}
          </select>
          {errors.quality_grade && <small className="text-danger">{errors.quality_grade}</small>}
        </div>
      </div>

      {/* Common fields (images and certificate) */}
      {renderCommonFields()}
    </div>
  );

  const renderFilamentForm = () => (
    <div className='row'>
      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Category<sup>*</sup></label>
          <select
            name="filament_categories"
            value={formData.filament_categories}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Category...</option>
            {filamentCategoriesOptions.map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
          {errors.filament_categories && <small className="text-danger">{errors.filament_categories}</small>}
        </div>
      </div>

   {/*   <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Price Per KG From<sup>*</sup></label>
          <div className="input-with-icon">
            <input
              type="number"
              name="filament_priceperkg_from"
              value={formData.filament_priceperkg_from}
              onChange={handleInputChange}
              className="form-control border-0 bg-light px-4"
            />
            {selectedOption === "$ / kg" ? (
              <FaDollarSign size={20} color="green" />
            ) : (
              <FaBox size={20} color="blue" />
            )}
          </div>
          {errors.filament_priceperkg_from && <small className="text-danger">{errors.filament_priceperkg_from}</small>}
        </div>
      </div>*/}

   {/*   <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Price Per KG To<sup>*</sup></label>
          <div className="input-with-icon">
            <input
              type="number"
              name="filament_priceperkg_to"
              value={formData.filament_priceperkg_to}
              onChange={handleInputChange}
              className="form-control border-0 bg-light px-4"
            />
            {selectedOption === "$ / kg" ? (
              <FaDollarSign size={20} color="green" />
            ) : (
              <FaBox size={20} color="blue" />
            )}
          </div>
          {errors.filament_priceperkg_to && <small className="text-danger">{errors.filament_priceperkg_to}</small>}
        </div>
      </div>*/}

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Quantity(Box) From</label>
          <input
            type="number"
            name="filament_quantity_from"
            value={formData.filament_quantity_from}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_quantity_from && <small className="text-danger">{errors.filament_quantity_from}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Quantity(Box) To</label>
          <input
            type="number"
            name="filament_quantity_to"
            value={formData.filament_quantity_to}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_quantity_to && <small className="text-danger">{errors.filament_quantity_to}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Semi/Full Dull</label>
          <select
            name="semi_full_dull"
            value={formData.semi_full_dull}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Semi/Full Dull...</option>
            {semiFullDullOptions.map((option, index) => (
              <option key={index} value={option.value}>{option.label}</option>
            ))}
          </select>
          {errors.semi_full_dull && <small className="text-danger">{errors.semi_full_dull}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Texturized</label>
          <select
            name="texturized"
            value={formData.texturized}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Texturized...</option>
            {texturizedOptions.map((option, index) => (
              <option key={index} value={option.value}>{option.label}</option>
            ))}
          </select>
          {errors.texturized && <small className="text-danger">{errors.texturized}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Dennier From</label>
          <input
            type="number"
            name="filament_dennier_from"
            value={formData.filament_dennier_from}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_dennier_from && <small className="text-danger">{errors.filament_dennier_from}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Dennier To</label>
          <input
            type="number"
            name="filament_dennier_to"
            value={formData.filament_dennier_to}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_dennier_to && <small className="text-danger">{errors.filament_dennier_to}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">No. Of Filament From</label>
          <input
            type="number"
            name="filament_no_from"
            value={formData.filament_no_from}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_no_from && <small className="text-danger">{errors.filament_no_from}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">No. Of Filament To</label>
          <input
            type="number"
            name="filament_no_to"
            value={formData.filament_no_to}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.filament_no_to && <small className="text-danger">{errors.filament_no_to}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Filament Twist</label>
          <input
            type="text"
            name="filament_twist"
            value={formData.filament_twist}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Roto Twist Intermingle</label>
          <input
            type="text"
            name="roto_twist_intermingle"
            value={formData.roto_twist_intermingle}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">IPI</label>
          <input
            type="text"
            name="ipi"
            value={formData.ipi}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          />
          {errors.ipi && <small className="text-danger">{errors.ipi}</small>}
        </div>
      </div>

      <div className='col-lg-6'>
        <div className="form-group">
          <label className="form-label text-dark">Quality Grade<sup>*</sup></label>
          <select
            name="quality_grade"
            value={formData.quality_grade}
            onChange={handleInputChange}
            className="form-control border-0 bg-light px-4"
          >
            <option value="">Choose Quality Grade...</option>
            {qualityGradeOptions.map((grade, index) => (
              <option key={index} value={grade}>{grade}</option>
            ))}
          </select>
          {errors.quality_grade && <small className="text-danger">{errors.quality_grade}</small>}
        </div>
      </div>



      

      {/* Common fields (images and certificate) */}
      {renderCommonFields()}
    </div>
  );

  const renderCommonFields = () => (
    <>
      <div className='col-lg-12'>
        <div className="form-group">
          <label className="form-label text-dark">Product Images <span className="badge bg-primary">jpg, png only</span></label>
          <input
            type="file"
            multiple
            accept="image/jpeg, image/png"
            onChange={handleImageChange}
            className="form-control border-0 bg-light px-4"
          />
          <div className='row'>
            <div className='col-lg-6'>
              <div className='card p-2 mt-3'>
                <label className=''>Previous Images</label>
                <div className='uploaded-images-list d-flex'>
                  {(inventoryData.prevImages || []).map((image, index) => (
                    <div className="d-flex mt-2" key={index}>
                      <div className="mr-2">
                        <img
                          src={baseUrl + image}
                          alt="preview"
                          className="img-thumbnail"
                          style={{ width: 100, height: 100 }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          
            {formData.images.length > 0 && (
              <div className='col-lg-6'>
                <div className='card p-2 mt-3'>
                  <label className=''>New Images</label>
                  {formData.images.map((image, index) => (
                    <DeleteImages
                      key={index}
                      images={isLocal ? image : baseUrl + image}
                      onDelete={() => handleImageDelete(index)}
                      isSingle
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {errors.images && <small className="text-danger">{errors.images}</small>}
        </div>
      </div>
      
      <div className='col-lg-12'>
        <div className="form-group certificate-field">
          <label className="form-label text-dark">Upload Certificate <span className="badge bg-primary">pdf only</span></label>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => handleFileChange(e, 'certificate')}
            className="form-control border-0 bg-light px-4"
          />
          {formData.certificate && (
            <>
              <FileSpreadsheet size={24} color="#6c757d" />
              <button
                type="button"
                className="btn btn-sm btn-danger ms-2"
                onClick={handleCertificateDelete}
              >
                Remove
              </button>
            </>
          )}
          {errors.certificate && <small className="text-danger">{errors.certificate}</small>}
        </div>
      </div>
      
      <div className='col-lg-12'>
        <button
          type="submit"
          className="btn btn-secondary py-3 px-5 mt-3"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </>
  );

  return (
    <div className="form-wrapper py-5">
      <div className="auth-container container">
        <div className="card form-card p-4">
          <h2 className="text-primary mb-4">Edit Inventory</h2> 
          <div className='price-type-select'>
            <div className='price-select-wrap ml-auto' style={{maxWidth: "250px"}}>
              <select className='form-select w-100' value={selectedOption} onChange={handleChange}>
                <option value="$ / kg">$/kg</option>
                <option value="Rp Jt/bale inc ppn">Rp Jt/bale inc ppn</option>
              </select>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            {inventoryData?.type === 'Spun' ? renderSpunForm() : renderFilamentForm()}
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditInventoryForm;